import { DatePipe } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ErrorHandler,
  inject,
  Injectable,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  type ApplicationConfig
} from '@angular/core';
import { TitleStrategy, provideRouter, withNavigationErrorHandler } from '@angular/router';
import { ChunkLoadErrorHandler } from '@core/handlers/chunk-load-error.handler';
import { APP_BUILD_VERSION } from '../environments/version';
import { TranslocoTitleStrategy } from '@core/services/title-strategy.service';
import { SupportedLanguage } from '@core/types/supported-language';
import { provideTransloco, type TranslocoLoader } from '@jsverse/transloco';
import { type Observable, retry } from 'rxjs';
import { routes } from './app.routes';
import { authInterceptor, provideOidcAuth } from './auth/auth-config';

@Injectable({ providedIn: 'root' })
class TranslocoHttpLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);

  getTranslation(lang: string): Observable<Record<string, string>> {
    return this.http.get<Record<string, string>>(`/assets/i18n/${lang}.json?v=${APP_BUILD_VERSION}`).pipe(retry(1));
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    DatePipe,
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withInterceptors([authInterceptor()])),
    provideRouter(
      routes,
      withNavigationErrorHandler((error) => inject(ErrorHandler).handleError(error.error))
    ),
    provideOidcAuth(),
    provideTransloco({
      config: {
        availableLangs: [SupportedLanguage.IT, SupportedLanguage.EN],
        defaultLang: SupportedLanguage.IT,
        fallbackLang: SupportedLanguage.EN,
        reRenderOnLangChange: true,
        prodMode: !isDevMode()
      },
      loader: TranslocoHttpLoader
    }),
    {
      provide: ErrorHandler,
      useClass: ChunkLoadErrorHandler
    },
    {
      provide: TitleStrategy,
      useClass: TranslocoTitleStrategy
    }
  ]
};
